
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { mockServices } from '@/data/mockData';
import { useBooking } from '@/contexts/BookingContext';
import { Clock, Search, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import StaffStep from './StaffStep';
import TimeStep from './TimeStep';

const ServiceStep = () => {
  const { 
    selectedProvider, 
    selectedService, 
    setSelectedService,
    goToNextStep,
    selectedStaff,
    selectedDate,
    selectedTime
  } = useBooking();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Simulate loading state
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 600);
    
    return () => clearTimeout(timer);
  }, []);
  
  if (!selectedProvider) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="flex justify-center"
      >
        <Card className="glass-card p-8 text-center max-w-md">
          <p className="text-chrona-gray-400">Bitte wähle zuerst einen Anbieter.</p>
        </Card>
      </motion.div>
    );
  }
  
  const services = mockServices[selectedProvider.id] || [];
  
  // Filter services by search
  const filteredServices = services.filter(service => 
    searchQuery === '' || 
    service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.beschreibung.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Group services by category
  const servicesByCategory = filteredServices.reduce((acc, service) => {
    if (!acc[service.kategorie]) {
      acc[service.kategorie] = [];
    }
    acc[service.kategorie].push(service);
    return acc;
  }, {} as Record<string, typeof services>);
  
  // Get unique categories for tabs
  const categories = Object.keys(servicesByCategory);
  
  // Function to handle service selection
  const handleSelectService = (service) => {
    console.log("Service selected:", service.name);
    setSelectedService(service);
    // We won't automatically go to next step anymore
    // Instead we'll show the staff and time selection components
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  // Check if all required selections are made to complete step 2
  const isStep2Complete = selectedService && selectedStaff && selectedDate && selectedTime;

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="animate-fade-in"
    >
      {/* Service Selection Section */}
      <motion.div variants={itemVariants} className="mb-8">
        <h2 className="text-xl font-medium mb-6 text-chrona-text flex items-center">
          <span className="w-1.5 h-5 bg-chrona-accent rounded-full mr-2"></span>
          Wähle eine Leistung
        </h2>
        
        {/* Search bar */}
        <motion.div className="mb-6 relative" variants={itemVariants}>
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-chrona-gray-400" size={18} />
          <Input
            type="text"
            placeholder="Dienstleistung suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 bg-chrona-gray-800/40 backdrop-blur border-white/10 focus:border-chrona-accent/50 transition-all rounded-xl"
          />
        </motion.div>
        
        {/* Category tabs */}
        {categories.length > 1 && (
          <motion.div 
            className="flex overflow-x-auto gap-2 mb-6 pb-2 hide-scrollbar"
            variants={itemVariants}
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                activeCategory === null
                  ? 'bg-chrona-accent text-white shadow-md shadow-chrona-accent/20'
                  : 'bg-chrona-gray-800/60 hover:bg-chrona-gray-700/60 text-chrona-text border border-white/10'
              }`}
              onClick={() => setActiveCategory(null)}
            >
              Alle Leistungen
            </motion.button>
            
            {categories.map(category => (
              <motion.button
                key={category}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                  activeCategory === category
                    ? 'bg-chrona-accent text-white shadow-md shadow-chrona-accent/20'
                    : 'bg-chrona-gray-800/60 hover:bg-chrona-gray-700/60 text-chrona-text border border-white/10'
                }`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </motion.button>
            ))}
          </motion.div>
        )}
        
        {isLoading ? (
          <div className="space-y-6">
            {[1, 2].map((categoryIndex) => (
              <div key={categoryIndex} className="mb-8">
                <Skeleton className="h-6 w-32 mb-3 bg-chrona-gray-800/60 rounded-full" />
                <div className="space-y-4">
                  {[1, 2, 3].map((serviceIndex) => (
                    <Card key={serviceIndex} className="glass-card overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row justify-between">
                          <div className="flex-1">
                            <Skeleton className="h-6 w-3/4 mb-2 bg-chrona-gray-800/60 rounded-full" />
                            <Skeleton className="h-4 w-20 mb-2 bg-chrona-gray-800/60 rounded-full" />
                            <Skeleton className="h-4 w-full mb-2 bg-chrona-gray-800/60 rounded-full" />
                            <Skeleton className="h-4 w-2/3 bg-chrona-gray-800/60 rounded-full" />
                          </div>
                          <div className="md:w-40 mt-4 md:mt-0 flex md:flex-col md:items-end">
                            <Skeleton className="h-8 w-20 bg-chrona-gray-800/60 rounded-full" />
                            <Skeleton className="h-10 w-28 mt-2 bg-chrona-gray-800/60 rounded-full hidden md:block" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <motion.div 
            className="space-y-6"
            variants={containerVariants}
          >
            {Object.entries(servicesByCategory)
              .filter(([category]) => activeCategory === null || category === activeCategory)
              .map(([category, services]) => (
                <motion.div key={category} className="mb-8" variants={itemVariants}>
                  <h2 className="text-lg font-medium mb-3 text-chrona-text flex items-center">
                    <span className="w-1.5 h-5 bg-chrona-accent rounded-full mr-2"></span>
                    {category}
                  </h2>
                  <div className="space-y-4">
                    {services.map((service) => (
                      <motion.div key={service.id} variants={itemVariants}>
                        <Card
                          className={`cursor-pointer hover:shadow-xl transition-all duration-300 glass-card card-3d ${
                            selectedService?.id === service.id ? 'ring-2 ring-chrona-accent shadow-xl shadow-chrona-accent/10' : ''
                          }`}
                          onClick={() => setSelectedService(service)}
                        >
                          <CardContent className="p-5">
                            <div className="flex flex-col md:flex-row justify-between">
                              <div>
                                <h3 className="font-medium text-lg text-chrona-text">{service.name}</h3>
                                <div className="flex items-center text-sm text-chrona-gray-400 mt-1">
                                  <Clock size={14} className="mr-1" />
                                  <span>{service.dauer} Min.</span>
                                </div>
                                <p className="text-sm text-chrona-gray-300 mt-2">{service.beschreibung}</p>
                                
                                {service.addons_aktiv.length > 0 && (
                                  <div className="mt-3 flex flex-wrap gap-1">
                                    {service.addons_aktiv.map((addon) => (
                                      <span 
                                        key={addon}
                                        className="inline-flex items-center px-2 py-1 bg-chrona-accent/20 text-chrona-accent text-xs rounded-full border border-chrona-accent/30"
                                      >
                                        <Check size={10} className="mr-1" />
                                        {addon}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              <div className="mt-4 md:mt-0 flex md:flex-col md:items-end md:min-w-[120px]">
                                <span className="text-xl font-bold text-chrona-accent">
                                  {service.preis.toFixed(2)} €
                                </span>
                                <Button
                                  size="sm"
                                  className="mt-3 bg-chrona-accent hover:bg-chrona-accent-light hidden md:inline-flex active:scale-95 transition-transform"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectService(service);
                                  }}
                                >
                                  Auswählen
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            
            {filteredServices.length === 0 && (
              <motion.div className="text-center py-12 glass-card rounded-2xl" variants={itemVariants}>
                <Search size={40} className="mx-auto mb-4 text-chrona-gray-600" />
                <p className="text-chrona-gray-400 mb-6">
                  Keine Leistungen gefunden.
                </p>
                {searchQuery && (
                  <Button 
                    onClick={() => setSearchQuery('')}
                    variant="outline"
                    className="border-white/10 hover:bg-white/10 hover:text-chrona-accent transition-colors"
                  >
                    Suche zurücksetzen
                  </Button>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
      </motion.div>

      {/* If service is selected, show staff selection component */}
      {selectedService && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h2 className="text-xl font-medium mb-6 text-chrona-text flex items-center">
            <span className="w-1.5 h-5 bg-chrona-accent rounded-full mr-2"></span>
            Wähle einen Mitarbeiter
          </h2>
          <StaffStep />
        </motion.div>
      )}

      {/* If service and staff are selected, show time selection component */}
      {selectedService && selectedStaff && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-xl font-medium mb-6 text-chrona-text flex items-center">
            <span className="w-1.5 h-5 bg-chrona-accent rounded-full mr-2"></span>
            Wähle einen Termin
          </h2>
          <TimeStep />
        </motion.div>
      )}
      
      {/* Complete step button - only visible when all selections are made */}
      {isStep2Complete && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-8 flex justify-end"
        >
          <Button
            className="bg-chrona-accent hover:bg-chrona-accent-light text-chrona-text shadow-md shadow-chrona-accent/20"
            onClick={goToNextStep}
          >
            Weiter zur Bestätigung
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ServiceStep;
